/**
 * useAdConfig Hook
 * Fetches global ad configuration and determines if current user should see ads
 */

import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthProvider";
import { apiGet } from "../lib/api";

export interface AdConfig {
  adsEnabled: boolean;
  testUserIds: string[];
}

export interface UseAdConfigReturn {
  adsEnabled: boolean;
  testUserIds: string[];
  showAdsForCurrentUser: boolean;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Hook to determine if ads should be displayed for the current user
 * 
 * Logic:
 * - If adsEnabled === true: show ads for everyone
 * - If adsEnabled === false: only show ads for test users (testUserIds)
 * - On error: fail-safe to not showing ads
 */
export function useAdConfig(): UseAdConfigReturn {
  const { user } = useAuth();
  const [config, setConfig] = useState<AdConfig>({ adsEnabled: false, testUserIds: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let mounted = true;

    const fetchConfig = async () => {
      try {
        const data = await apiGet<{
          ok: boolean;
          adsEnabled: boolean;
          testUserIds: string[];
        }>("/api/ad-config");

        if (mounted && data.ok) {
          setConfig({
            adsEnabled: data.adsEnabled,
            testUserIds: data.testUserIds || [],
          });
          setError(null);
        }
      } catch (err) {
        if (mounted) {
          console.error("[useAdConfig] Failed to fetch ad config:", err);
          setError(err instanceof Error ? err : new Error("Failed to fetch ad config"));
          // Fail-safe: no ads on error
          setConfig({ adsEnabled: false, testUserIds: [] });
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    fetchConfig();

    return () => {
      mounted = false;
    };
  }, []);

  // Compute if current user should see ads
  const showAdsForCurrentUser =
    config.adsEnabled === true
      ? true
      : user && config.testUserIds.includes(user.id);

  return {
    adsEnabled: config.adsEnabled,
    testUserIds: config.testUserIds,
    showAdsForCurrentUser: Boolean(showAdsForCurrentUser),
    isLoading,
    error,
  };
}
